import { baseApi } from '../../../core/baseApi';
import { createListQuery, createCreateMutation, createGetByIdQuery } from '../../../patterns/crud';
import { GetListArgs } from '../../../patterns/crud';
import type { Schema } from '@voley/shared-types';

type ExportLogsResponse = Schema<'ExportLogsListResponse'>;

export const adminExportsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Admin export management - custom query to handle { logs: [...] } response structure
    getExportLogs: builder.query<ExportLogsResponse, GetListArgs | void>({
      query: (params?: GetListArgs) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              searchParams.append(key, String(value));
            }
          });
        }
        const queryString = searchParams.toString();
        return `/exports/logs${queryString ? `?${queryString}` : ''}`;
      },
      transformResponse: (response: ExportLogsResponse): ExportLogsResponse => {
        // Backend returns { logs: [...], total, page, limit } - return as-is
        return response;
      },
      providesTags: (result) =>
        result?.logs
          ? [
              { type: 'ExportLogs', id: 'LIST' },
              ...result.logs.map((log) => ({ type: 'ExportLogs' as const, id: log.id }))
            ]
          : [{ type: 'ExportLogs', id: 'LIST' }],
    }),
    createExport: createCreateMutation('/exports/create', 'ExportLogs')(builder),
    getExportStatus: createGetByIdQuery('/exports/status', 'ExportStatus')(builder),
    
    // User export functionality (consolidated from exports/exports.ts)
    getTimeRangeOptions: createListQuery('/exports/time-ranges', 'Exports')(builder),
    exportConversation: builder.mutation<{ blob: Blob; filename: string }, { id: string; [key: string]: unknown }>({
      query: ({ id, ...params }) => ({
        url: `/exports/conversations/${id}/csv`,
        method: 'GET',
        params,
        responseHandler: async (response) => {
          // Extract filename from Content-Disposition header before consuming body
          const disposition = response.headers.get('Content-Disposition') || '';
          const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = filenameMatch?.[1] || `conversation-export-${new Date().toISOString().split('T')[0]}.csv`;
          const blob = await response.blob();
          return { blob, filename };
        },
      }),
    }),
    exportContactConversations: builder.mutation<Blob, { id: string; [key: string]: unknown }>({
      query: ({ id, ...params }) => ({
        url: `/exports/contacts/${id}/conversations/csv`,
        method: 'GET',
        params,
        responseHandler: async (response) => await response.blob(),
      }),
  
    }),
    exportBulkConversations: builder.mutation<Blob, unknown>({
      query: (params) => ({
        url: `/exports/conversations/bulk/csv`,
        method: 'POST',
        body: params,
        responseHandler: async (response) => await response.blob(),
              }),
    }),
    exportAllConversations: builder.mutation<Blob, unknown>({
      query: (params) => ({
        url: `/exports/conversations/csv`,
        method: 'GET',
        params: params as Record<string, unknown>,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    exportBulkContacts: builder.mutation<Blob, unknown>({
      query: (params) => ({
        url: `/exports/contacts/csv`,
        method: 'POST',
        body: params,
        responseHandler: async (response) => await response.blob(),
              }),
    }),
    exportAuditTrail: builder.mutation<{ blob: Blob; filename: string }, {
      tableName?: string;
      recordId?: string;
      operation?: 'INSERT' | 'UPDATE' | 'DELETE';
      changedBy?: string;
      startDate?: string;
      endDate?: string;
      timezone?: string;
    }>({
      query: (params) => ({
        url: `/exports/audit-trail/csv`,
        method: 'POST',
        body: params,
        responseHandler: async (response) => {
          const disposition = response.headers.get('Content-Disposition') || '';
          const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = filenameMatch?.[1] || `audit-trail-export-${new Date().toISOString().split('T')[0]}.csv`;
          const blob = await response.blob();
          return { blob, filename };
        },
      }),
    }),
  })
});

export const {
  useGetExportLogsQuery,
  useCreateExportMutation,
  useGetExportStatusQuery,
  useGetTimeRangeOptionsQuery,
  useExportConversationMutation,
  useExportContactConversationsMutation,
  useExportBulkConversationsMutation,
  useExportAllConversationsMutation,
  useExportBulkContactsMutation,
  useExportAuditTrailMutation,
} = adminExportsApi;